import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { langNameJa } from '../data/languages';
import { getCategory } from '../data/categories';
import { IconUserPlus, IconUserCheck } from '../components/talkAppleIcons';
import { PersonAvatar, PeopleHeader, SearchField, PeopleEmpty, PeopleLoading, usePeopleScroll } from '../components/PeopleUI';

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const view = params.get('view') === 'users' || query.trim() ? 'users' : 'recommended';
  const [users, setUsers] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recommendLoading, setRecommendLoading] = useState(true);
  const [error, setError] = useState('');
  const [recommendError, setRecommendError] = useState('');
  const [actionError, setActionError] = useState('');
  const [followBusyId, setFollowBusyId] = useState('');
  const [retry, setRetry] = useState(0);
  const following = useRef(false);
  usePeopleScroll(`search:${params.toString()}`, view === 'recommended' ? !recommendLoading : !loading);

  useEffect(() => {
    let active = true;
    setRecommendLoading(true); setRecommendError('');
    axios.get('/api/users/recommendations').then(({ data }) => { if (active) setRecommended(data); })
      .catch(() => { if (active) setRecommendError('おすすめを読み込めませんでした。'); })
      .finally(() => { if (active) setRecommendLoading(false); });
    return () => { active = false; };
  }, [retry]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoading(true); setError('');
    const timer = setTimeout(() => {
      axios.get('/api/users', { params: { q: query.trim() }, signal: controller.signal })
        .then(({ data }) => { if (active) setUsers(data); })
        .catch(() => { if (active) setError('ユーザーを読み込めませんでした。'); })
        .finally(() => { if (active) setLoading(false); });
    }, query.trim() ? 250 : 0);
    return () => { active = false; controller.abort(); clearTimeout(timer); };
  }, [query, retry]);

  const updateQuery = value => {
    setLoading(true);
    setParams(previous => { const next = new URLSearchParams(previous); value ? next.set('q', value) : next.delete('q'); return next; }, { replace: true });
  };
  const changeView = value => setParams(previous => { const next = new URLSearchParams(previous); next.delete('q'); next.set('view', value); return next; }, { replace: true });
  const toggleFollow = async person => {
    if (following.current) return;
    following.current = true; setFollowBusyId(person.id); setActionError('');
    try {
      await axios[person.is_following ? 'delete' : 'post'](`/api/users/${person.id}/follow`);
      const update = list => list.map(item => item.id === person.id ? { ...item, is_following: !person.is_following } : item);
      setUsers(update); setRecommended(update);
    } catch { setActionError('フォローを変更できませんでした。もう一度お試しください。'); }
    finally { following.current = false; setFollowBusyId(''); }
  };
  const list = view === 'recommended' ? recommended : users;
  const isLoading = view === 'recommended' ? recommendLoading : loading;
  const loadError = view === 'recommended' ? recommendError : error;

  return <main className="jt-screen jt-search-screen">
    <PeopleHeader title="相手を探す" />
    <div className="jt-toolbar">
      <SearchField value={query} onChange={updateQuery} placeholder="名前や自己紹介で検索" />
      <div className="jt-tabs" aria-label="ユーザー一覧の切り替え">
        <button data-control="tab" data-control-id="Search-tab" aria-pressed={view === 'recommended'} onClick={() => changeView('recommended')}>おすすめ</button>
        <button data-control="tab" data-control-id="Search-tab" aria-pressed={view === 'users'} onClick={() => changeView('users')}>{query ? '検索結果' : 'ユーザー'}</button>
      </div>
    </div>
    {actionError && <p className="jt-feedback" role="alert">{actionError}</p>}
    {isLoading ? <PeopleLoading /> : loadError ? <PeopleEmpty title={loadError} description="接続を確認して、もう一度お試しください。" onRetry={() => setRetry(value => value + 1)} /> : <>
      {list.length > 0 && <p className="jt-list-caption">{view === 'recommended' ? '言語や興味が近い人' : query ? `${list.length}人が見つかりました` : `${list.length}人のユーザー`}</p>}
      {list.map(person => <article className="jt-person-row" key={person.id}>
        <Link data-control="row" data-control-id="Search-jt-person-main" className="jt-person-main" to={`/profile/${person.id}`} aria-label={`${person.username}のプロフィール`}>
          <div className="jt-person-heading">
            <PersonAvatar person={person} />
            <div className="jt-person-copy">
              <span className="jt-person-name">{person.username}</span>
              <p className="jt-language">{langNameJa(person.native_lang)}<span aria-hidden="true"> → </span>{langNameJa(person.learning_lang)}を学習中</p>
            </div>
          </div>
          {person.bio && <p className="jt-person-bio">{person.bio}</p>}
          {person.interests?.length > 0 && <p className="jt-interests">{person.interests.slice(0, 3).map(id => getCategory(id)?.label).filter(Boolean).join('、')}</p>}
        </Link>
        <button data-control="icon" data-control-id="Search-jt-row-action" className="jt-row-action" type="button" aria-label={`${person.username}を${person.is_following ? 'フォロー解除' : 'フォロー'}`} aria-pressed={!!person.is_following} disabled={!!followBusyId} onClick={() => toggleFollow(person)}>
          {followBusyId === person.id ? '…' : person.is_following ? <IconUserCheck size={20} /> : <IconUserPlus size={20} />}
        </button>
      </article>)}
      {list.length === 0 && <PeopleEmpty title={query ? '見つかりませんでした' : view === 'recommended' ? '興味のあることを教えてください' : 'ユーザーはまだいません'} description={query ? '名前の一部や、別のキーワードで試してみてください。' : view === 'recommended' ? '興味を登録すると、話の合う相手を探しやすくなります。' : '時間をおいて、また見にきてください。'} to={!query && view === 'recommended' ? '/settings' : undefined} action="興味を登録する" />}
    </>}
  </main>;
}
